<template>
<form @submit.prevent="login" class="authorization-form">
    <div class="grid-2">
        <EmailInput :aria-required="true" v-model="loginForm.email" :errors="loginForm.errors.email" id="email_login" ref="emailInput"/>
        <PasswordInput :aria-required="true" v-model="loginForm.password" :errors="loginForm.errors.password" id="email_password" ref="passwordInput"/>

        <div v-if="errors.login" class="notification notification-error">
            <p class="paragraph-details"><strong>Error:</strong> {{errors.login }}</p>
        </div>
        <Recaptcha class="recaptcha-wrapper" :key="recaptchaKey" v-if="isLoginError" v-model="recaptchaResponse" theme="light"/>
        <Link v-if="isFlyout" :href="route('web.forgot-password')" class="navigation authorization-forgot_password">Forgot Password?</Link>
        <RothcoCta :disabled="loginForm.processing" class="primary authorization-button" tabindex="0" id="loginBtn" type="submit">
            Login
        </RothcoCta>
    </div>
</form>
</template>

<script setup lang="ts">
    import { ref, watch, computed, inject} from "vue";
    import { object, string} from 'yup';
    import {  usePage, Link } from "@inertiajs/vue3";
    import { useForm } from "@/helpers/composables/useForm";
    import EmailInput from "@/components/EmailInput.vue";
    import PasswordInput from "@/components/PasswordInput.vue";
    import RothcoCta from "@/components/RothcoCta.vue";
    import Recaptcha from "@/components/Recaptcha.vue";
    
    // import { route } from "ziggy-js";
    const isLoginError = ref(false);
    const isLoginSuccess = ref(false);
    let route = inject('route');
    const loginForm = useForm({
        useInertia: true,
        schema: object({
            email: string().email('Must be a valid email').required('The email field is required'),
            password: string().required('The password field is required')
        })
    });



    let errors = ref({});

    const emit = defineEmits(["setAsLoggedIn"]);

    const setAsLoggedIn = () => {
        emit("setAsLoggedIn");
    };

    let recaptchaResponse = ref(null);
    let recaptchaKey = ref(1);
    const { inputClasses, inputWrapperClasses, buttonClasses, isFlyout, d2cOnly } =
        defineProps({
            isFlyout: {
                type: Boolean,
                default: false,
                required: false
            },
            d2cOnly: {
                type: Boolean,
                default: false
            }
        });



    const login = async () => {
        if(loginForm.processing) return;

        if((isLoginError.value && recaptchaResponse.value) || !isLoginError.value){
            await loginForm.validate();
        }
        if((!loginForm.hasErrors && !isLoginError.value) || (isLoginError.value && recaptchaResponse.value)){
            loginForm.transform((data)=>({
                ...(d2cOnly ? {
                    'login-email': data.email,
                    'login-password': data.password,
                    'g-recaptcha-response': recaptchaResponse.value
                } : {
                    ...data,
                    'g-recaptcha-response': recaptchaResponse.value
                }),
                flyout: isFlyout,
                _token: usePage().props.csrf_token,
            })).post(
                route(d2cOnly ? "authenticate" : "web.login"),
                {   
                    preserveState: (page) => {
                        return Object.keys(page.props.errors).length > 0;
                    },
                    onSuccess: (page) => {
                        setAsLoggedIn();
                        isLoginSuccess.value = true;
                    },
                    onError: () => {
                        recaptchaResponse.value = null;
                        recaptchaKey.value++;
                        isLoginError.value = true;
                        errors.value = usePage().props.errors;
                    }
                }
            );
        }
    };

    watch(() => loginForm.email, () => {
        if(loginForm.hasErrors){
            loginForm.clearErrors('email');
            loginForm.clearErrors('login');
        }
    });

    watch(() => loginForm.password, () => {
        if(loginForm.errors.password || loginForm.errors.login){
            loginForm.clearErrors('password');
            loginForm.clearErrors('login');
        }
    });
</script>
<style></style>
